import { Card, Skeleton, Table, theme, Button } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { CustomDatePicker } from '../../components/CustomDatePicker';
import { SecureGet } from '../../services/axiosCall';
import apis from '../../services/Apis';
import { useNavigate } from 'react-router-dom';
import { TableCol } from './getTableCol';
import useEvaluationRequestScope from '../../hooks/useEvaluationRequestScope';

const { useToken } = theme;


const StatCard = ({ label, value, accent, icon, tok }) => (
  <div style={{
    background: tok.colorBgContainer,
    border: `1px solid ${accent}33`,
    borderRadius: 14,
    padding: '16px 20px',
    position: 'relative',
    overflow: 'hidden',
    flex: 1,
    minWidth: 140,
  }}>
    <div style={{
      position: 'absolute', top: 0, right: 0,
      width: 50, height: 50,
      borderRadius: '0 14px 0 50px',
      background: `${accent}10`,
    }} />
    <div style={{ fontSize: 20, marginBottom: 6 }}>{icon}</div>
    <div style={{ fontSize: 11, fontWeight: 600, color: tok.colorTextQuaternary, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      {label}
    </div>
    <div style={{ fontSize: 26, fontWeight: 800, color: accent, lineHeight: 1.2, letterSpacing: '-0.5px' }}>
      {value}
    </div>
  </div>
);

const FacultyResults = () => {
  const { token: tok } = useToken();
  const navigate = useNavigate();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(Number(currentYear) -1);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [departments, setDepartments] = useState([]);

  const scope = useEvaluationRequestScope(String(selectedYear));
  const handleSelectedYear = (_, yearString) => {
    setData([]);
    setDepartments([]);
    setLoading(true);
    setSelectedYear(Number(yearString));
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  }

  const fetchResults = useCallback(() => {
    const isCurrent = scope.capture();
    setData([]);
    setDepartments([]);
    setLoading(true);
    SecureGet({
      url: apis.APA_RESULTS_FACULTY_ADMIN,
      params: { year: selectedYear,  limit: 1000 },
    })
      .then((res) => {
        if (!isCurrent()) return;
        setData(res.data?.results ?? []);
        // Extract unique departments
        const uniqueDepartments = [...new Set((res?.data?.results ?? []).map(item => item?.user?.department))];
        setDepartments(uniqueDepartments);
      })
      .catch(() => { if (isCurrent()) setData([]); })
      .finally(() => { if (isCurrent()) setLoading(false); });
  }, [selectedYear]);

  useEffect(() => { fetchResults(); }, [selectedYear]);

  // ── Summary stats ──────────────────────────────────────────────
  const total = data.length;
  const avgFinal = total
    ? (data.reduce((s, r) => s + (r.final_apa_point ?? 0), 0) / total).toFixed(2)
    : '—';
  const topScore = total
    ? Math.max(...data.map(r => r.final_apa_point ?? 0)).toFixed(2)
    : '—';


  const columns = [
    ...TableCol(
      searchText,
      setSearchText,
      searchedColumn,
      setSearchedColumn,
      departments
    ),
    {
      title: '',
      key: 'action',
      width: 80,
      render: (_, row) => {
        const user = row.user ?? {};
        return (
          <Button
            type="primary"
            onClick={() => navigate(`/faculty/${user?.id}`, { state: { row: user, year: selectedYear } })}
          >
            View Details
          </Button>
        );
      },
    },
  ];

  return (
    <Card style={{ borderRadius: 8, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>

      {/* ── Page Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap', gap: 12, marginBottom: 24,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: tok.colorText, letterSpacing: '-0.3px' }}>
            Faculty APA Results
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: 13, color: tok.colorTextSecondary }}>
            Annual Performance Assessment · {selectedYear}
          </p>
        </div>
        <CustomDatePicker
          selectedYear={selectedYear}
          handleSelectedYear={handleSelectedYear}

        />
      </div>

      {/* ── Summary Cards ── */}
      {loading ? (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[1, 2, 3].map(i => (
            <Skeleton.Input key={i} active block style={{ height: 90, borderRadius: 14, flex: 1 }} />
          ))}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatCard label="Total Faculties" value={total} accent="#6366f1" icon="👥" tok={tok} />
          <StatCard label="Average Score" value={avgFinal} accent="#10b981" icon="📊" tok={tok} />
          <StatCard label="Top Score" value={topScore} accent="#f59e0b" icon="🏆" tok={tok} />
        </div>
      )}

      {/* ── Table ── */}
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={columns}
        onChange={handleTableChange}
        pagination={pagination}
        scroll={{ x: 900 }}
        locale={{
          emptyText: (
            <div style={{ padding: '40px 0', color: tok.colorTextTertiary, textAlign: 'center' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📭</div>
              No faculty results found for {selectedYear}.
            </div>
          ),
        }}
        rowClassName={(_, i) => i % 2 === 0 ? 'row-even' : 'row-odd'}
      />
    </Card>
  );
};

export default FacultyResults;